import { Button, IconButton, Modal, Textarea } from "@design";
import { useMutation } from "@tanstack/react-query";
import { FileText, Paperclip, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { errorMessage, type FlowResponse, streamFlowRun, uploadFlowFiles } from "@/api";
import { Text } from "@/components/Text";
import { useOperationStream } from "@/streaming/useOperationStream";
import { flowRunPath } from "./flowRunPaths";
import { flowRunStreamReducer, initialFlowRunStream } from "./flowRunStreamReducer";
import { useRefreshRunViews } from "./useRefreshRunViews";

/** Where the run page reads the live operation from, so it can show progress without waiting for a refetch. */
export interface FlowRunLocationState {
  opId?: string;
}

/**
 * Start a workflow by hand: an input prompt (prefilled with the workflow's default) and optional files.
 * Once the run exists (flow_start), opens its page, which follows it live.
 */
export function RunFlowDialog({
  flow,
  open,
  onClose,
  runPath = flowRunPath,
}: Readonly<{
  flow: FlowResponse;
  open: boolean;
  onClose: () => void;
  /** Where a run's page lives (the Build area by default). */
  runPath?: (flowUuid: string, runUuid: string) => string;
}>) {
  const { t } = useTranslation("workflows");
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const [prompt, setPrompt] = useState(flow.defaultInputPrompt ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [opId, setOpId] = useState<string | null>(null);
  const live = useOperationStream(opId, flowRunStreamReducer, initialFlowRunStream);
  const refreshRunViews = useRefreshRunViews();

  const start = useMutation({
    mutationFn: async () => {
      const uploaded = files.length
        ? (await uploadFlowFiles({ path: { flow_uuid: flow.uuid }, body: { files }, throwOnError: true })).data.files
        : [];
      const { data } = await streamFlowRun({
        path: { flow_uuid: flow.uuid },
        body: { inputPrompt: prompt.trim(), fileBlobPaths: uploaded.map((f) => f.blob_path) },
        throwOnError: true,
      });
      return data.operation_id;
    },
    onSuccess: setOpId,
  });

  const runUuid = live.state.runUuid;
  const opened = useRef(false);
  useEffect(() => {
    if (!opId || !runUuid || opened.current) return;
    opened.current = true;
    // The run exists now: lists and space cards show it as running.
    void refreshRunViews(flow.uuid);
    navigate(runPath(flow.uuid, runUuid), { state: { opId } satisfies FlowRunLocationState });
    onClose();
  }, [opId, runUuid, flow.uuid, navigate, onClose, runPath, refreshRunViews]);

  // Failed before the run existed (budget, no steps...): nothing to open, so say so here.
  const failedEarly = opId !== null && !runUuid && (live.state.status === "failed" || live.status === "failed");
  let error: string | null = null;
  if (start.isError) error = errorMessage(start.error, t("runs.runFailed"));
  else if (failedEarly) error = live.state.error ?? t("runs.runFailed");
  const starting = start.isPending || (opId !== null && !runUuid && !failedEarly);

  // Stable: Modal re-runs its focus handling whenever onClose changes, which would pull focus out of the prompt.
  const resetStart = start.reset;
  const close = useCallback(() => {
    if (starting) return;
    setOpId(null);
    resetStart();
    onClose();
  }, [starting, resetStart, onClose]);

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("runDialog.title", { name: flow.name })}
      closeLabel={t("runDialog.cancel")}
      actions={
        <>
          <Button variant="secondary" onClick={close} disabled={starting}>
            {t("runDialog.cancel")}
          </Button>
          <Button
            variant="primary"
            loading={starting}
            onClick={() => {
              setOpId(null);
              start.mutate();
            }}
          >
            {t("runDialog.run")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="run-flow-prompt">
            <Text variant="body-sm">{t("runDialog.prompt")}</Text>
          </label>
          <Textarea
            id="run-flow-prompt"
            rows={5}
            value={prompt}
            placeholder={t("runDialog.promptPlaceholder")}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <Text variant="caption" tone="tertiary">
            {t("runDialog.promptHelp")}
          </Text>
        </div>
        <div className="flex flex-col gap-2">
          {files.length > 0 && (
            <ul className="flex flex-col gap-1" aria-label={t("runDialog.files")}>
              {files.map((file, i) => (
                <li key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center gap-2">
                  <FileText size={16} aria-hidden className="shrink-0 text-content-tertiary" />
                  <Text variant="body-sm" className="min-w-0 flex-1 truncate">
                    {file.name}
                  </Text>
                  <IconButton
                    icon={<X />}
                    variant="ghost"
                    size="sm"
                    aria-label={t("runDialog.removeFile", { name: file.name })}
                    onClick={() => setFiles((all) => all.filter((_, j) => j !== i))}
                  />
                </li>
              ))}
            </ul>
          )}
          <div>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Paperclip size={14} aria-hidden />}
              onClick={() => fileInput.current?.click()}
            >
              {t("runDialog.attach")}
            </Button>
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            aria-hidden
            tabIndex={-1}
            onChange={(e) => {
              const chosen = Array.from(e.target.files ?? []);
              setFiles((all) => [...all, ...chosen]);
              e.target.value = "";
            }}
          />
        </div>
        {error && (
          <Text variant="body-sm" tone="error" role="alert">
            {error}
          </Text>
        )}
      </div>
    </Modal>
  );
}
